'use client';

import { Button } from '@heroui/button';
import { Form } from '@heroui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ControlledEmailInput } from '@/components/auth/controlled-email-input';
import { ControlledPasswordInput } from '@/components/auth/controlled-password-input';
import { FormSubmitButton } from '@/components/auth/form-submit-button';
import { VerifyEmailDialog } from '@/components/auth/verify-email-dialog';
import { authClient } from '@/lib/auth-client';
import { useLoadingCallback } from '@/lib/hooks/use-loading-callback';
import { authToasts } from '@/lib/utils/toast';
import { loginSchema } from '@/schemas/auth';
import { PasskeyLoginButton } from './passkey-login-button';

type FormData = z.infer<typeof loginSchema>;

interface PasswordLoginFormProps {
    onSwitchToOtp: () => void;
}

export function PasswordLoginForm({ onSwitchToOtp }: PasswordLoginFormProps) {
    const router = useRouter();
    const [isVerifyEmailDialogOpen, setIsVerifyEmailDialogOpen] =
        useState(false);
    const [unverifiedEmail, setUnverifiedEmail] = useState('');

    const passwordForm = useForm<FormData>({
        resolver: zodResolver(loginSchema),
        mode: 'onChange', // Real-time validation
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const [handlePasswordLogin, passwordLoading] = useLoadingCallback(
        async (data: FormData) => {
            await authClient.signIn.email(data, {
                onSuccess: (context) => {
                    if (context.data.twoFactorRedirect) {
                        // Handle 2FA redirect
                        router.push('/auth/login/2fa');
                        return;
                    }

                    // Normal login success
                    authToasts.loginSuccess();
                    router.push('/account/settings');
                },
                onError: (context) => {
                    if (context.error.code === 'EMAIL_NOT_VERIFIED') {
                        authToasts.loginFailed(
                            'Your email is not verified. Please verify your email to log in.'
                        );
                        setUnverifiedEmail(data.email);
                        setIsVerifyEmailDialogOpen(true);
                    } else {
                        authToasts.loginFailed(context.error.message);
                    }
                },
            });
        }
    );

    return (
        <div className="flex flex-col gap-4">
            <Form
                className="flex flex-col gap-4"
                onSubmit={passwordForm.handleSubmit(handlePasswordLogin)}
            >
                <ControlledEmailInput
                    control={passwordForm.control}
                    name="email"
                />
                <ControlledPasswordInput
                    control={passwordForm.control}
                    name="password"
                />
                <FormSubmitButton
                    color="primary"
                    control={passwordForm.control}
                    fullWidth
                    isLoading={passwordLoading}
                >
                    Log In
                </FormSubmitButton>
            </Form>
            <div className="relative my-2 flex w-full items-center">
                <div className="h-px flex-grow bg-default-200/50" />
                <span className="mx-4 text-default-400 text-small">or</span>
                <div className="h-px flex-grow bg-default-200/50" />
            </div>

            <div className="flex flex-row justify-between gap-2">
                <PasskeyLoginButton />
                <Button
                    fullWidth
                    onPress={onSwitchToOtp}
                    startContent={<Mail className="h-4 w-4" />}
                    variant="bordered"
                >
                    Use an OTP
                </Button>
            </div>

            {isVerifyEmailDialogOpen && (
                <VerifyEmailDialog
                    isClosable={false}
                    isOpen={isVerifyEmailDialogOpen}
                    onOpenChange={setIsVerifyEmailDialogOpen} // Pass minimal session data
                    session={{ user: { email: unverifiedEmail } }}
                />
            )}
        </div>
    );
}
